import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { Subscription } from '@/hooks/useSubscriptions';

const subscriptionSchema = z.object({
  plan_name: z.string().min(1, 'Plan name is required'),
  base_monthly_fee: z.string().refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
    message: 'Must be a positive number',
  }),
  per_user_monthly_fee: z.string().refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
    message: 'Must be a positive number',
  }),
  licensed_user_count: z.string()
    .min(1, 'Licensed user count is required')
    .refine((val) => !isNaN(Number(val)) && Number(val) >= 1, {
      message: 'Must be at least 1',
    }),
  billing_cycle_start_date: z.string().min(1, 'Start date is required'),
  next_billing_date: z.string().min(1, 'Next billing date is required'),
  subscription_status: z.enum(['active', 'suspended', 'cancelled', 'trial']),
  trial_end_date: z.string().optional(),
  notes: z.string().optional(),
});

type SubscriptionFormValues = z.infer<typeof subscriptionSchema>;

interface SubscriptionFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscription?: Subscription | null;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
  userCount?: number;
}

export function SubscriptionForm({
  open,
  onOpenChange,
  subscription,
  onSubmit,
  isSubmitting,
  userCount = 0,
}: SubscriptionFormProps) {
  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      plan_name: '',
      base_monthly_fee: '0',
      per_user_monthly_fee: '0',
      licensed_user_count: '1',
      billing_cycle_start_date: format(new Date(), 'yyyy-MM-dd'),
      next_billing_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      subscription_status: 'active',
      trial_end_date: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (subscription) {
      form.reset({
        plan_name: subscription.plan_name,
        base_monthly_fee: subscription.base_monthly_fee.toString(),
        per_user_monthly_fee: subscription.per_user_monthly_fee.toString(),
        licensed_user_count: subscription.licensed_user_count.toString(),
        billing_cycle_start_date: subscription.billing_cycle_start_date,
        next_billing_date: subscription.next_billing_date,
        subscription_status: subscription.subscription_status,
        trial_end_date: subscription.trial_end_date || '',
        notes: subscription.notes || '',
      });
    }
  }, [subscription, form]);

  const estimatedCost = useMemo(() => {
    const baseFee = Number(form.watch('base_monthly_fee')) || 0;
    const perUserFee = Number(form.watch('per_user_monthly_fee')) || 0;
    const licensedUsers = Number(form.watch('licensed_user_count')) || 0;
    return baseFee + (perUserFee * licensedUsers);
  }, [form.watch('base_monthly_fee'), form.watch('per_user_monthly_fee'), form.watch('licensed_user_count')]);

  const handleSubmit = (values: SubscriptionFormValues) => {
    const data = {
      ...values,
      base_monthly_fee: Number(values.base_monthly_fee),
      per_user_monthly_fee: Number(values.per_user_monthly_fee),
      licensed_user_count: Number(values.licensed_user_count),
      trial_end_date: values.trial_end_date || null,
      notes: values.notes || null,
    };

    if (subscription) {
      onSubmit({ id: subscription.id, ...data });
    } else {
      onSubmit(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {subscription ? 'Edit Subscription' : 'Create Subscription'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="plan_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Standard, Enterprise" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="base_monthly_fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Base Monthly Fee ($)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="per_user_monthly_fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Per User Monthly Fee ($)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="licensed_user_count"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Licensed User Count</FormLabel>
                  <FormControl>
                    <Input type="number" min="1" placeholder="10" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="bg-muted p-4 rounded-md">
              <h4 className="text-sm font-semibold mb-2">Estimated Cost</h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Base Monthly Fee:</span>
                  <span className="font-medium">${Number(form.watch('base_monthly_fee') || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Per User Fee (${Number(form.watch('per_user_monthly_fee') || 0).toFixed(2)} × {Number(form.watch('licensed_user_count') || 0)} licensed):</span>
                  <span className="font-medium">${(Number(form.watch('per_user_monthly_fee') || 0) * Number(form.watch('licensed_user_count') || 0)).toFixed(2)}</span>
                </div>
                {userCount > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Actual users in system:</span>
                    <span>{userCount}</span>
                  </div>
                )}
                <div className="border-t border-border pt-1 mt-2 flex justify-between font-semibold">
                  <span>Total Monthly Cost:</span>
                  <span>${estimatedCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>Annual Projection:</span>
                  <span>${(estimatedCost * 12).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="billing_cycle_start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Cycle Start</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="next_billing_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Next Billing Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="subscription_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('subscription_status') === 'trial' && (
              <FormField
                control={form.control}
                name="trial_end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trial End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : subscription ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
